import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, User, Phone, Mail, MapPin, FileText, Tag, ArrowLeft, CreditCard, Search } from 'lucide-react';
import CustomerDatePicker from './CustomerDatePicker';

const CustomerScheduling = ({
  currentPage,
  setCurrentPage,
  services,
  appointments,
  setAppointments,
  coupons,
  bundles = [],
  blockedDates = [],
  createAppointment,
  adminSettings
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    // Optional SMS consent, tracked independently. Both default to false and
    // stay unchecked until the customer actively opts in — carriers reject
    // pre-checked consent, and reject bundling these two together.
    smsTransactionalConsent: false,
    smsMarketingConsent: false,
    address: '',
    city: '',
    state: '',
    zip: '',
    selectedServiceIds: [],
    date: '',
    time: '',
    notes: '',
    couponCode: '',
    notificationPreference: 'email',
    // Add these new fields for AC covering
    acUnitHeight: '',
    acUnitWidth: '',
    // Payment preference
    wantsToPayOnline: false
  });

  // Bundle auto-detection state
  const [detectedBundle, setDetectedBundle] = useState(null);

  const [availableTimes, setAvailableTimes] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastCreatedAppointment, setLastCreatedAppointment] = useState(null);
  const [lookupValue, setLookupValue] = useState('');
  const [lookupStatus, setLookupStatus] = useState(null); // null | 'loading' | 'found' | 'not_found' | 'multiple'
  const [lookupName, setLookupName] = useState('');
  const [lookupResults, setLookupResults] = useState([]);


  const isACCoveringService = () => {
    if (formData.selectedServiceIds.length === 0) return false;
    return formData.selectedServiceIds.some(sid => {
      const svc = services.find(s => s.id.toString() === sid.toString());
      return svc && (
        svc.name.toLowerCase().includes('ac covering') ||
        svc.name.toLowerCase().includes('ac-covering')
      );
    });
  };

  // Service toggle handler for multi-select checkboxes
  const handleServiceToggle = (serviceId) => {
    const sid = serviceId.toString();
    setFormData(prev => {
      const current = prev.selectedServiceIds.map(String);
      const updated = current.includes(sid)
        ? current.filter(id => id !== sid)
        : [...current, sid];
      return { ...prev, selectedServiceIds: updated, date: '', time: '' };
    });
    setAvailableTimes([]);
  };

  // Bundle auto-detection effect
  useEffect(() => {
    if (formData.selectedServiceIds.length < 2 || !bundles || bundles.length === 0) {
      setDetectedBundle(null);
      return;
    }

    const selectedSet = new Set(formData.selectedServiceIds.map(String));

    const match = bundles.find(bundle => {
      if (!bundle.active) return false;
      const bundleSet = new Set(bundle.service_ids.map(String));
      if (bundleSet.size !== selectedSet.size) return false;
      for (const id of bundleSet) {
        if (!selectedSet.has(id)) return false;
      }
      return true;
    });

    setDetectedBundle(match || null);
    // Clear coupon when bundle is detected — cannot combine
    if (match) {
      setAppliedCoupon(null);
      setCouponError('');
    }
  }, [formData.selectedServiceIds, bundles]);

  // Helper: get selected services objects
  const getSelectedServices = () => {
    return formData.selectedServiceIds
      .map(sid => services.find(s => s.id.toString() === sid.toString()))
      .filter(Boolean);
  };

  // Helper: calculate totals
  const calculatePricing = () => {
    const selected = getSelectedServices();
    const subtotal = selected.reduce((sum, s) => sum + parseFloat(s.price), 0);
    const totalDuration = selected.reduce((sum, s) => sum + (parseInt(s.duration) || 45), 0);
    let bundleDiscount = 0;
    let priceAfterBundle = subtotal;

    if (detectedBundle) {
      priceAfterBundle = parseFloat(detectedBundle.bundle_price);
      bundleDiscount = subtotal - priceAfterBundle;
    }

    // Coupons cannot be combined with bundle deals
    let couponDiscount = 0;
    if (appliedCoupon && !detectedBundle) {
      if (appliedCoupon.discount_type === 'percentage') {
        couponDiscount = priceAfterBundle * (appliedCoupon.discount_value / 100);
      } else {
        couponDiscount = appliedCoupon.discount_value;
      }
    }

    const finalPrice = Math.max(0, priceAfterBundle - couponDiscount);

    return { subtotal, bundleDiscount, priceAfterBundle, couponDiscount, finalPrice, totalDuration, selected };
  };

  // Handle URL parameters for service pre-selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const serviceParam = urlParams.get('service');

    if (serviceParam && services.length > 0) {
      // Try to find a matching service based on the parameter
      let matchingService = null;

      if (serviceParam === 'sprinkler') {
        matchingService = services.find(s =>
          s.name.toLowerCase().includes('sprinkler') ||
          s.name.toLowerCase().includes('irrigation')
        );
      } else if (serviceParam === 'plumbing') {
        matchingService = services.find(s => s.name.toLowerCase().includes('plumb'));
      } else if (serviceParam === 'electrical') {
        matchingService = services.find(s =>
          s.name.toLowerCase().includes('electric') ||
          s.name.toLowerCase().includes('electrical')
        );
      } else if (serviceParam === 'general') {
        matchingService = services.find(s =>
          s.name.toLowerCase().includes('handyman') ||
          s.name.toLowerCase().includes('repair') ||
          s.name.toLowerCase().includes('general')
        );
      }

      // If we found a matching service, pre-select it
      if (matchingService) {
        setFormData(prev => ({ ...prev, selectedServiceIds: [matchingService.id.toString()] }));
      }
    }
  }, [services]);

  // Load available times when date or selected services change
  useEffect(() => {
    if (formData.date && formData.selectedServiceIds.length > 0) {
      loadAvailableTimes(formData.date, formData.selectedServiceIds);
    }
  }, [formData.date, formData.selectedServiceIds]);

  const loadAvailableTimes = async (date, serviceIds) => {
    try {
      const serviceParam = serviceIds.length > 1
        ? `service_ids=${serviceIds.join(',')}`
        : `service_id=${serviceIds[0]}`;
      const response = await fetch(`/scheduler/api/availability.php?date=${date}&${serviceParam}`);
      const result = await response.json();
      if (result.success) {
        // Ensure we have valid time data
        const times = result.data || [];
        console.log('Raw times from API:', times);

        // Normalize the time format - handle different possible formats
        const normalizedTimes = times.map(timeItem => {
          if (typeof timeItem === 'string') {
            return timeItem; // Simple string format like "14:00:00"
          } else if (typeof timeItem === 'object' && timeItem.value) {
            return timeItem.value; // Object format like {value: "14:00:00", display: "2:00 PM"}
          } else if (typeof timeItem === 'object' && timeItem.time) {
            return timeItem.time; // Another possible format
          }
          return null; // Invalid format
        }).filter(time => time !== null);

        console.log('Normalized times:', normalizedTimes);
        setAvailableTimes(normalizedTimes);
      } else {
        console.error('Failed to load times:', result.error);
        setAvailableTimes([]);
      }
    } catch (error) {
      console.error('Error loading available times:', error);
      setAvailableTimes([]);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Format based on length
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)})${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    } else {
      // Limit to 10 digits
      return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const fillFromResult = (source, data) => {
    if (source === 'customers') {
      const d = data;
      let address = d.address_line1 || d.address || '';
      let city = d.city || '';
      let state = d.state || '';
      let zip = d.zip || '';

      // If customer record has no address, check their most recent appointment
      if (!address) {
        const custEmail = (d.email || '').toLowerCase().trim();
        const custPhone = (d.phone || '').replace(/\D/g, '');
        const pastApt = [...appointments].reverse().find(a => {
          const aEmail = (a.customer_email || '').toLowerCase().trim();
          const aPhone = (a.customer_phone || '').replace(/\D/g, '');
          return (custEmail && aEmail === custEmail) || (custPhone && aPhone === custPhone);
        });
        if (pastApt && pastApt.service_address) {
          const parts = pastApt.service_address.split(',').map(s => s.trim());
          address = parts[0] || '';
          city = parts[1] || city;
          if (parts[2]) {
            const stateZip = parts[2].trim().split(/\s+/);
            state = stateZip[0] || state;
            zip = stateZip[1] || zip;
          }
        }
      }

      setFormData(prev => ({
        ...prev,
        firstName: d.first_name || '',
        lastName: d.last_name || '',
        email: d.email || '',
        phone: d.phone ? formatPhoneNumber(d.phone) : '',
        address,
        city,
        state,
        zip,
        acUnitHeight: d.ac_unit_height ? String(d.ac_unit_height) : '',
        acUnitWidth: d.ac_unit_width ? String(d.ac_unit_width) : ''
      }));
      setLookupName(`${d.first_name} ${d.last_name}`);
    } else {
      // appointments source — parse combined fields
      const d = data;
      const nameParts = (d.customer_name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      let address = '', city = '', state = '', zip = '';
      if (d.service_address) {
        const parts = d.service_address.split(',').map(s => s.trim());
        address = parts[0] || '';
        city = parts[1] || '';
        if (parts[2]) {
          const stateZip = parts[2].trim().split(/\s+/);
          state = stateZip[0] || '';
          zip = stateZip[1] || '';
        }
      }

      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: d.customer_email || '',
        phone: d.customer_phone ? formatPhoneNumber(d.customer_phone) : '',
        address,
        city,
        state,
        zip
      }));
      setLookupName(`${firstName} ${lastName}`);
    }
  };

  const selectLookupResult = (result) => {
    fillFromResult(result.source, result.data);
    setLookupResults([]);
    setLookupStatus('found');
  };

  const handleLookup = async () => {
    if (!lookupValue.trim()) return;
    setLookupStatus('loading');
    setLookupResults([]);
    try {
      const response = await fetch(`/scheduler/api/customers.php?lookup=${encodeURIComponent(lookupValue.trim())}`);
      const result = await response.json();
      if (result.success && result.found) {
        if (result.multiple) {
          // Multiple matches — show selection list
          setLookupResults(result.results);
          setLookupStatus('multiple');
        } else {
          // Single match — auto-fill immediately
          fillFromResult(result.source, result.data);
          setLookupStatus('found');
        }
      } else {
        setLookupStatus('not_found');
      }
    } catch (error) {
      console.error('Customer lookup error:', error);
      setLookupStatus('not_found');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Auto-format phone number
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear time when date changes
    if (name === 'date') {
      setFormData(prev => ({ ...prev, time: '' }));
    }

    // Note: service selection now handled by handleServiceToggle, not handleInputChange
  };

  // Services carrying a live coupon float to the top of the picker.
  // The list scrolls (max-h-64), so a seasonal special sitting below the fold
  // is a special the customer never finds — that is how people kept missing
  // Sprinkler Winterization. Keyed by String(service.id): coupon.service_id
  // arrives as a number, service.id sometimes as a string.
  const promotedCouponsByService = useMemo(() => {
    const now = new Date();
    const map = new Map();
    (coupons || []).forEach(coupon => {
      if (!coupon || !coupon.active) return;
      // A site-wide coupon applies to everything, so it promotes nothing in particular.
      if (!coupon.service_id) return;
      const expiry = coupon.expiry_date || coupon.expiryDate;
      if (expiry && new Date(expiry) < now) return;
      const limit = coupon.usage_limit ?? coupon.usageLimit;
      const used = coupon.usage_count ?? coupon.usageCount ?? 0;
      if (limit && used >= limit) return;
      const key = String(coupon.service_id);
      if (!map.has(key)) map.set(key, coupon);
    });
    return map;
  }, [coupons]);

  // Promoted first, everything else in its original order.
  const orderedServices = useMemo(() => {
    const active = (services || []).filter(s => s.active !== false);
    const promoted = active.filter(s => promotedCouponsByService.has(String(s.id)));
    const rest = active.filter(s => !promotedCouponsByService.has(String(s.id)));
    return [...promoted, ...rest];
  }, [services, promotedCouponsByService]);

  // The service list is a fixed-height scroller, so the hint below it stays up
  // until the customer has actually reached the bottom of the list.
  const [servicesScrolledToEnd, setServicesScrolledToEnd] = useState(false);

  const handleServiceListScroll = (e) => {
    const el = e.target;
    setServicesScrolledToEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 8);
  };

  const handleCouponApply = () => {
    const coupon = coupons.find(c =>
      c.code.toLowerCase() === formData.couponCode.toLowerCase() &&
      c.active &&
      new Date(c.expiry_date || c.expiryDate) > new Date()
    );

    if (coupon) {
      // Check service-specific restriction
      if (coupon.service_id && !formData.selectedServiceIds.includes(String(coupon.service_id)) && !formData.selectedServiceIds.includes(coupon.service_id)) {
        const serviceName = services.find(s => s.id === coupon.service_id || s.id === String(coupon.service_id))?.name || 'a specific service';
        setAppliedCoupon(null);
        setCouponError(`This coupon is only valid for ${serviceName}`);
        return;
      }

      // Normalize property names for consistency
      const normalizedCoupon = {
        ...coupon,
        discount_type: coupon.type,
        discount_value: coupon.value
      };
      setAppliedCoupon(normalizedCoupon);
      setCouponError('');
    } else {
      setAppliedCoupon(null);
      setCouponError('Invalid or expired coupon code');
    }
  };

  const sendEmailNotification = async (appointmentData, type) => {
    try {
      await fetch('/scheduler/api/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentData, type })
      });
    } catch (error) {
      console.error(`Error sending ${type} email:`, error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedServices = getSelectedServices();
    if (selectedServices.length === 0) {
      alert('Please select at least one service');
      setIsSubmitting(false);
      return;
    }

    const pricing = calculatePricing();
    const isMulti = selectedServices.length > 1;

    const appointmentData = {
      customer_name: `${formData.firstName} ${formData.lastName}`,
      customer_phone: formData.phone,
      customer_email: formData.email,
      sms_marketing_consent: formData.smsMarketingConsent ? 1 : 0,
      sms_transactional_consent: formData.smsTransactionalConsent ? 1 : 0,
      service_address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
      // Multi-service: send service_ids array; single: send service_id
      ...(isMulti
        ? { service_ids: formData.selectedServiceIds.map(Number) }
        : {
            service_id: formData.selectedServiceIds[0],
            service_name: selectedServices[0].name,
            service_price: parseFloat(selectedServices[0].price)
          }
      ),
      appointment_date: formData.date,
      appointment_time: formData.time,
      final_price: pricing.finalPrice,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
      discount_amount: pricing.couponDiscount + pricing.bundleDiscount,
      // Bundle info
      bundle_id: detectedBundle ? detectedBundle.id : null,
      bundle_name: detectedBundle ? detectedBundle.name : null,
      bundle_discount: pricing.bundleDiscount,
      notes: formData.notes,
      notification_preference: 'email',
      status: 'scheduled',
      ac_unit_height: formData.acUnitHeight || null,
      ac_unit_width: formData.acUnitWidth || null,
      wants_to_pay_online: formData.wantsToPayOnline
    };

    if (isACCoveringService()) {
      if (!formData.acUnitHeight || !formData.acUnitWidth) {
        alert('Please enter both height and width measurements for your AC unit.');
        setIsSubmitting(false);
        return;
      }
    }

    const result = await createAppointment(appointmentData);

    if (result && result.success) {
      setLastCreatedAppointment(result.data);
      setAppointments(prev => [...prev, result.data]);

      // Send email notifications
      await sendEmailNotification(result.data, 'customer');
      await sendEmailNotification(result.data, 'admin');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        selectedServiceIds: [],
        date: '',
        time: '',
        notes: '',
        couponCode: '',
        notificationPreference: 'email',
        acUnitHeight: '',
        acUnitWidth: '',
        wantsToPayOnline: false
      });
      setDetectedBundle(null);
      setAppliedCoupon(null);
      setCouponError('');
      setIsSubmitting(false);

      // Check if customer wants to pay online and redirect accordingly
      if (formData.wantsToPayOnline) {
        setCurrentPage('payment');
      } else {
        setCurrentPage('confirmation');
      }
    } else if (result && result.conflict) {
      // Someone booked this slot while the form was open. Refresh the list so
      // the taken time disappears, and send the customer back to pick again.
      alert(result.error || 'That time is no longer available. Please choose another time.');
      setFormData(prev => ({ ...prev, time: '' }));
      if (formData.date && formData.selectedServiceIds.length > 0) {
        await loadAvailableTimes(formData.date, formData.selectedServiceIds);
      }
      setIsSubmitting(false);
    } else {
      alert('Failed to create appointment: ' + (result?.error || 'Unknown error'));
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateStr, error);
      return dateStr;
    }
  };

  // FIXED: Safe time formatting with better error handling
  const formatTime = (timeStr) => {
    if (!timeStr) return '';

    // Safety check: ensure timeStr is a string
    if (typeof timeStr !== 'string') {
      console.warn('formatTime received non-string value:', timeStr, typeof timeStr);
      return String(timeStr); // Convert to string as fallback
    }

    // Check if the string contains a colon
    if (!timeStr.includes(':')) {
      console.warn('formatTime: time string missing colon:', timeStr);
      return timeStr; // Return as-is if no colon found
    }

    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);

      // Validate that hour is a number
      if (isNaN(hour)) {
        console.warn('formatTime: invalid hour value:', hours);
        return timeStr;
      }

      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error in formatTime:', error, 'Input:', timeStr);
      return String(timeStr);
    }
  };
  // Get service category for styling
  const getServiceCategory = (serviceName) => {
    const name = serviceName.toLowerCase();
    if (name.includes('sprinkler') || name.includes('irrigation')) return 'sprinkler';
    if (name.includes('plumb')) return 'plumbing';
    if (name.includes('electric') || name.includes('electrical')) return 'electrical';
    return 'general';
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      sprinkler: 'green',
      plumbing: 'blue',
      electrical: 'yellow',
      general: 'gray'
    };
    return colorMap[category] || 'gray';
  };

  // Scheduling Page
  if (currentPage === 'schedule') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">

            {/* Header with Back Button */}
            <div className="mb-6">
              <button
                onClick={() => setCurrentPage('welcome')}
                className="flex items-center text-green-600 hover:text-green-700 font-medium mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Services
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-green-800 mb-2">Schedule Your Appointment</h1>
                <p className="text-gray-600">Fill out the form below to book your service</p>

                {/* Debug info in development */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-left">
                    <strong>Debug:</strong> {coupons.length} coupons loaded, {coupons.filter(c => c.active).length} active
                    <br />
                    <strong>Available times:</strong> {availableTimes.length} times loaded
                    <br />
                    <strong>Times:</strong> {JSON.stringify(availableTimes.slice(0, 3))}
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-8">
                {/* Left Column - Form */}
                <div className="space-y-6">
                  {/* Returning Customer Lookup */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Returning Customer?</h3>
                    <p className="text-xs text-gray-500 mb-3">Enter your phone number or email to auto-fill your details.</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={lookupValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Auto-format if it looks like a phone number (starts with digit or parenthesis)
                          if (/^[\d(]/.test(val) && !val.includes('@')) {
                            setLookupValue(formatPhoneNumber(val));
                          } else {
                            setLookupValue(val);
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
                        placeholder="Phone or email"
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <button
                        type="button"
                        onClick={handleLookup}
                        disabled={lookupStatus === 'loading' || !lookupValue.trim()}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center gap-1"
                      >
                        <Search className="w-4 h-4" />
                        {lookupStatus === 'loading' ? 'Looking up...' : 'Look Up'}
                      </button>
                    </div>
                    {lookupStatus === 'found' && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                        Welcome back, <strong>{lookupName}</strong>! Your details have been filled in below.
                      </div>
                    )}
                    {lookupStatus === 'multiple' && lookupResults.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-700 mb-2">Multiple accounts found. Which one is yours?</p>
                        <div className="space-y-2">
                          {lookupResults.map((result, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => selectLookupResult(result)}
                              className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors"
                            >
                              <span className="font-medium text-gray-800">{result.name}</span>
                              <span className="text-gray-500 text-sm ml-2">{result.email}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {lookupStatus === 'not_found' && (
                      <div className="mt-3 p-3 bg-gray-100 border border-gray-200 rounded text-sm text-gray-600">
                        No account found — please fill in your details below.
                      </div>
                    )}
                  </div>

                  {/* Name Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-1" />
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  {/* Two INDEPENDENT optional SMS opt-ins, both unchecked by default and
                      never required to book. A2P 10DLC error 30913 rejects any flow that
                      bundles marketing consent with transactional consent, so these must
                      never be merged or made to imply each other. Wording must stay in
                      sync with /sms-terms.html. */}
                  <div className="space-y-3">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="smsTransactionalConsent"
                          checked={formData.smsTransactionalConsent}
                          onChange={handleInputChange}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-600">
                          <strong className="text-gray-800">Appointment texts.</strong>{' '}
                          I agree to receive text messages from AllTech LLC at the mobile number
                          provided about appointments I book, including confirmations, reminders,
                          and scheduling updates. Message and data rates may apply. Message
                          frequency varies. Reply STOP to opt out or HELP for help. See our{' '}
                          <a href="/sms-terms.html" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">SMS Terms</a>
                          {' '}and{' '}
                          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Privacy Policy</a>.
                        </span>
                      </label>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="smsMarketingConsent"
                          checked={formData.smsMarketingConsent}
                          onChange={handleInputChange}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-600">
                          <strong className="text-gray-800">Service specials.</strong>{' '}
                          I agree to receive marketing text messages from AllTech LLC at the mobile
                          number provided, including service specials and promotional offers.
                          Message and data rates may apply. Message frequency varies, up to 4
                          messages per month. Reply STOP to opt out or HELP for help. See our{' '}
                          <a href="/sms-terms.html" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">SMS Terms</a>
                          {' '}and{' '}
                          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Privacy Policy</a>.
                          Consent is not a condition of purchase.
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Service Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Street Address"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 mb-3"
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="City"
                        className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        placeholder="State"
                        className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        required
                        placeholder="ZIP"
                        className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Any specific details or requirements..."
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                {/* Right Column - Service Selection */}
                <div className="space-y-6">
                  {/* Service Selection - Multi-Select Checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Service(s) *
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select one or more services. Bundle discounts apply automatically!
                      {promotedCouponsByService.size > 0 && ' Current specials are listed first.'}
                    </p>
                    {orderedServices.length > 3 && (
                      <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                        <span aria-hidden="true">↕</span>
                        Scroll inside the box below to see all {orderedServices.length} services.
                      </p>
                    )}
                    <div
                      onScroll={handleServiceListScroll}
                      className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3"
                    >
                      {orderedServices.map(service => {
                        const isSelected = formData.selectedServiceIds.map(String).includes(service.id.toString());
                        const promoCoupon = promotedCouponsByService.get(String(service.id));
                        return (
                          <label
                            key={service.id}
                            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'bg-green-50 border-2 border-green-400'
                                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleServiceToggle(service.id)}
                              disabled={isSubmitting}
                              className="mt-1 text-green-600 focus:ring-green-500 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <span className="font-medium text-gray-800">
                                  {service.name}
                                  {promoCoupon && (
                                    <span className="ml-2 inline-block align-middle text-xs font-bold uppercase tracking-wide bg-yellow-400 text-yellow-900 rounded-full px-2 py-0.5">
                                      Special
                                    </span>
                                  )}
                                </span>
                                <span className="font-bold text-green-600">${parseFloat(service.price).toFixed(2)}</span>
                              </div>
                              {promoCoupon && (
                                <div className="text-xs font-semibold text-yellow-700 mt-1">
                                  Save {promoCoupon.type === 'percentage'
                                    ? `${parseFloat(promoCoupon.value).toFixed(0)}%`
                                    : `$${parseFloat(promoCoupon.value).toFixed(2)}`} with code {promoCoupon.code}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                {service.duration || 45} min
                                {service.description && ` - ${service.description}`}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {orderedServices.length > 3 && !servicesScrolledToEnd && (
                      <p className="text-xs text-blue-700 mt-1 text-center">
                        ↓ More services below — scroll the list
                      </p>
                    )}
                    {formData.selectedServiceIds.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">Please select at least one service</p>
                    )}
                  </div>

                  {/* Bundle Detection Banner */}
                  {detectedBundle && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🎉</span>
                        <h4 className="font-bold text-yellow-800">Bundle Deal Applied!</h4>
                      </div>
                      <p className="text-sm text-yellow-700">
                        <strong>{detectedBundle.name}</strong> — You save ${parseFloat(detectedBundle.savings || 0).toFixed(2)}!
                      </p>
                      {detectedBundle.description && (
                        <p className="text-xs text-yellow-600 mt-1">{detectedBundle.description}</p>
                      )}
                    </div>
                  )}

                  {/* Coupon Code */}
                  <div>
                    {detectedBundle ? (
                      <p className="text-sm text-gray-500 italic">
                        <Tag className="w-4 h-4 inline mr-1" />
                        Coupon codes cannot be combined with bundle deals.
                      </p>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Tag className="w-4 h-4 inline mr-1" />
                          Coupon Code (Optional)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            name="couponCode"
                            value={formData.couponCode}
                            onChange={handleInputChange}
                            placeholder="Enter coupon code"
                            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                          <button
                            type="button"
                            onClick={handleCouponApply}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            Apply
                          </button>
                        </div>
                        {couponError && (
                          <p className="text-red-600 text-sm mt-1">{couponError}</p>
                        )}
                        {appliedCoupon && (
                          <p className="text-green-600 text-sm mt-1">
                            Coupon applied: {appliedCoupon.discount_value}
                            {appliedCoupon.discount_type === 'percentage' ? '%' : '$'} off
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* ADD THE AC COVERING FIELDS HERE - RIGHT AFTER SERVICE SELECTION */}
                  {/* AC Unit Dimensions - Always visible, required only for AC covering services */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      AC Unit Measurements
                    </h3>
                    <p className="text-xs text-blue-600 mb-3">
                      Please measure your outdoor AC unit (required for AC covering services).
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Height (inches) {isACCoveringService() && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="number"
                          name="acUnitHeight"
                          value={formData.acUnitHeight}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., 30"
                          min="12"
                          max="60"
                          required={isACCoveringService()}
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-gray-500 mt-1">Typical range: 24-36 inches</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Width (inches) {isACCoveringService() && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="number"
                          name="acUnitWidth"
                          value={formData.acUnitWidth}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., 30"
                          min="18"
                          max="48"
                          required={isACCoveringService()}
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-gray-500 mt-1">Typical range: 24-36 inches</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs text-yellow-800">
                        <strong>💡 Measuring Tip:</strong> Measure the widest points of your outdoor unit.
                        {isACCoveringService() ?
                          ' Required for proper cover sizing.' :
                          ' Optional but helpful for future AC services.'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Date Selection - Smart Calendar */}
                  {formData.selectedServiceIds.length > 0 ? (
                    <CustomerDatePicker
                      selectedDate={formData.date}
                      onDateSelect={(dateStr) => {
                        setFormData(prev => ({ ...prev, date: dateStr, time: '' }));
                        setAvailableTimes([]);
                      }}
                      blockedDates={blockedDates.filter(bd => bd.is_entire_date_blocked)}
                      serviceIds={formData.selectedServiceIds}
                      isSubmitting={isSubmitting}
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Select Date *</h3>
                      <p className="text-sm text-gray-500">Please select a service first to see available dates.</p>
                    </div>
                  )}

                  {/* Time Selection - FIXED */}
                  {availableTimes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Available Times *
                      </label>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {availableTimes.map((time, index) => (
                          <label key={`${time}-${index}`} className="flex items-center">
                            <input
                              type="radio"
                              name="time"
                              value={time}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-sm">{formatTime(time)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price Summary */}
                  {formData.selectedServiceIds.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-semibold text-green-800 mb-2">Price Summary</h3>
                      {(() => {
                        const pricing = calculatePricing();

                        return (
                          <div className="space-y-1 text-sm">
                            {/* List each selected service */}
                            {pricing.selected.map(svc => (
                              <div key={svc.id} className="flex justify-between">
                                <span>{svc.name} ({svc.duration || 45} min)</span>
                                <span>${parseFloat(svc.price).toFixed(2)}</span>
                              </div>
                            ))}

                            {pricing.selected.length > 1 && (
                              <div className="flex justify-between pt-1 border-t border-green-200">
                                <span>Subtotal ({pricing.totalDuration} min):</span>
                                <span>${pricing.subtotal.toFixed(2)}</span>
                              </div>
                            )}

                            {/* Bundle discount */}
                            {detectedBundle && pricing.bundleDiscount > 0 && (
                              <div className="flex justify-between text-yellow-700 font-medium">
                                <span>Bundle Deal ({detectedBundle.name}):</span>
                                <span>-${pricing.bundleDiscount.toFixed(2)}</span>
                              </div>
                            )}

                            {/* Coupon discount */}
                            {appliedCoupon && pricing.couponDiscount > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Coupon ({appliedCoupon.code}):</span>
                                <span>-${pricing.couponDiscount.toFixed(2)}</span>
                              </div>
                            )}

                            <div className="flex justify-between font-bold text-green-800 pt-1 border-t border-green-300">
                              <span>Total:</span>
                              <span>${pricing.finalPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Payment Option */}
                  {formData.selectedServiceIds.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-800 mb-3">Payment Options</h3>
                      <div className="space-y-3">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            name="wantsToPayOnline"
                            checked={formData.wantsToPayOnline}
                            onChange={(e) => setFormData(prev => ({ ...prev, wantsToPayOnline: e.target.checked }))}
                            className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <div className="font-medium text-blue-800 flex items-center">
                              <CreditCard className="w-4 h-4 mr-2" />
                              Pay with Credit Card
                            </div>
                            <p className="text-sm text-blue-600 mt-1">
                              Secure online payment after booking confirmation
                            </p>
                          </div>
                        </label>

                        {!formData.wantsToPayOnline && (
                          <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded border-l-4 border-gray-400">
                            <strong>Pay Later:</strong> You can pay cash, check, or Venmo at the time of service
                          </div>
                        )}

                        {formData.wantsToPayOnline && (
                          <div className="text-sm text-blue-600 bg-blue-100 p-3 rounded border-l-4 border-blue-400">
                            <strong>Secure Payment:</strong> You'll be redirected to our secure payment page after booking
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting || formData.selectedServiceIds.length === 0 || !formData.date || !formData.time}
                      className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Scheduling...' : 'Schedule Appointment'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation Page
  if (currentPage === 'confirmation') {
    const appointmentToShow = lastCreatedAppointment || appointments[appointments.length - 1];

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Appointment Confirmed!</h2>
            <p className="text-gray-600 mb-6">Your appointment has been successfully scheduled.</p>

            {appointmentToShow && (
              <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-green-800 mb-2">Appointment Details:</h3>
                <div className="space-y-1 text-sm text-green-700">
                  <p><strong>Service(s):</strong> {appointmentToShow.service_names || appointmentToShow.service_name}</p>
                  <p><strong>Date:</strong> {formatDate(appointmentToShow.appointment_date)}</p>
                  <p><strong>Time:</strong> {formatTime(appointmentToShow.appointment_time)}</p>
                  {appointmentToShow.bundle_name && (
                    <p><strong>Bundle Deal:</strong> {appointmentToShow.bundle_name} (Save ${parseFloat(appointmentToShow.bundle_discount || 0).toFixed(2)})</p>
                  )}
                  {appointmentToShow.coupon_code ? (
                    <>
                      <p><strong>Original Price:</strong> ${parseFloat(appointmentToShow.service_price || 0).toFixed(2)}</p>
                      <p><strong>Coupon:</strong> {appointmentToShow.coupon_code}</p>
                      <p><strong>Discount:</strong> -${parseFloat(appointmentToShow.discount_amount || 0).toFixed(2)}</p>
                      <p><strong>Final Price:</strong> ${parseFloat(appointmentToShow.final_price || 0).toFixed(2)}</p>
                    </>
                  ) : (
                    <p><strong>Price:</strong> ${parseFloat(appointmentToShow.final_price || 0).toFixed(2)}</p>
                  )}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 mb-6">
              A confirmation email has been sent to your email address.
              You'll also receive a reminder 24 hours before your appointment.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setCurrentPage('welcome');
                  setLastCreatedAppointment(null);
                  // Clear URL parameters
                  window.history.pushState({}, '', window.location.pathname);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Back to Home
              </button>

              <button
                onClick={() => {
                  setCurrentPage('schedule');
                  setLastCreatedAppointment(null);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Schedule Another Appointment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CustomerScheduling;